import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import PopModal from "../../../Constant/Custom/Models/PopModal";
import { serverUrl } from "../../../Constant/Static/config";
import { AuthContext } from "../../../Auth/AuthContext";
import Loader from "../../../Constant/Custom/Loader";

function ImagesUpload({ handlePrevious, handleNext }) {
  const { auth } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);

  // Start of Section IV  -   Photo & Signature Upload
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [signaturePreview, setSignaturePreview] = useState("");
  const storedUser = auth.user;

  const [onCancel, setOnCancel] = useState(() => () => { });
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState({ title: "", text: "", icon: "" });
  const [onConfirm, setOnConfirm] = useState(() => () => { });

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const handleFileChange = async (e) => {
    setShowCancelButton(false)
    const { name, files } = e.target;
    const file = files[0];
    // console.log(`${name}|${file}`);
    // Validate file type and size
    const validTypes = ["image/jpeg", "image/jpg"];
    const maxPhotoSize = 51 * 1024; // 50 KB
    const maxSignatureSize = 31 * 1024; // 30 KB

    if (!file || !validTypes.includes(file.type)) {
      setModalType({
        title: "Invalid image format",
        text: "Please upload a valid image (JPG/JPEG).",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    }
    if (name === "photo" && file.size > maxPhotoSize) {
      setModalType({
        title: "Invalid Photo",
        text: "Please upload a valid photo (JPG/JPEG) under 50KB.",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    } else if (name === "signature" && file.size > maxSignatureSize) {
      setModalType({
        title: "Invalid Signature",
        text: "Please upload a valid signature (JPG/JPEG) under 30KB.",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    }

    const formData = new FormData();
    formData.append("appno", storedUser?.appno);
    formData.append("mobile", storedUser?.mobile);
    formData.append(name, file);

    try {
      setLoading(true);
      const response = await fetch(`${serverUrl}/api/upload`, {
        method: "POST",
        body: formData,
      });

      if (response.status === 200) {
        const result = await response.json();
        if (result.photoUrl) setPhotoPreview(result.photoUrl);
        if (result.signatureUrl) setSignaturePreview(result.signatureUrl);
        // console.log("sign", result.signatureUrl);

        setModalType({
          icon: "success",
          title: "Success",
          text: `${name === "photo" ? "Photo" : "Signature"
            } Uploaded Successfully`,
        });
        openModal();
        setOnConfirm(() => closeModal);
        // console.log(formData.get(name));
      } else {
        // alert('Failed to upload files.');
        setModalType({
          icon: "info",
          title: "Info",
          text: "Failed to upload files. Refresh & Try Again",
        });
        openModal();
        setOnConfirm(() => closeModal);
      }
    } catch (err) {
      console.error("Error uploading files", err);
      setModalType({
        icon: "error",
        title: "Error",
        text: "Error in Uploading Files. Please Try Again later",
      });
      openModal();
      setOnConfirm(() => closeModal);
    } finally {
      setLoading(false);
    }

    // console.log(signaturePreview, photoPreview);
  };

  const handleFileDelete = async (fileUrl, deletedField) => {
    try {
      // console.log("Deleting file:", fileUrl);
      if (!deletedField) {
        console.warn("No matching field found for fileUrl.");
        return;
      }

      // Define the delete handler WITH the needed context
      const deleteFile = async () => {
        try {
          if (deletedField === "photo") {
            setPhotoPreview("");
          }
          if (deletedField === "sign") {
            setSignaturePreview("");
          }
          setLoading(true);
          const response = await axios.post(`${serverUrl}/api/delete-image`, {
            fileUrl,
            filename: deletedField,
            appno: storedUser.appno,
            mobile: storedUser.mobile,
          });

          // console.log(
          //   "File deleted successfully:",
          //   response.data?.message || response.statusText
          // );
        } catch (error) {
          console.error("Error deleting file on server:", error);
        } finally {
          setLoading(false);
          closeModal();
        }
      };

      // Show modal
      setModalType({
        icon: "warning",
        title: `${deletedField.charAt(0).toUpperCase() + deletedField.slice(1)} Deletion`,
        text: `You want to delete this ${deletedField} ?`,
      });

      setShowCancelButton(true);
      openModal();
      setOnCancel(() => closeModal);
      setOnConfirm(() => () => deleteFile());
    } catch (error) {
      console.error("Error preparing file deletion:", error);
    }
  };

  const handleImageUploadSubmit = () => {
    setShowCancelButton(false);
    if (photoPreview !== "" && signaturePreview !== "") {
      setModalType({
        icon: "success",
        title: "success",
        text: "Photo and Signature Uploaded successfully",
      });
      openModal();
      setOnConfirm(() => handleNext);
    } else {
      setModalType({
        icon: "warning",
        title: "Warning",
        text: "Please Upload Both Photo and Signature",
      });
      openModal();
      setOnConfirm(() => closeModal);
    }
  };

  // Fetch existing uploaded images on component mount
  useEffect(() => {
    const storedUser = auth.user;
    const fetchUploadedImages = async () => {
      localStorage.setItem("currentSection", 5);
      try {
        setLoading(true);
        const response = await axios.post(`${serverUrl}/api/fetchUploadedImages`, {
          appno: storedUser.appno,
          mobile: storedUser.mobile,
        });
        if (response.data.photoUrl) {
          setPhotoPreview(response.data.photoUrl);
        }
        if (response.data.signatureUrl) {
          setSignaturePreview(response.data.signatureUrl);
        }
      } catch (err) {
        console.error("Error fetching uploaded images", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUploadedImages();
  }, []);
  if (loading) {
    return <Loader />;
  }

  // End of Section IV

  return (
    <section>
      <div className="section-head-container">
        <h2 className="section-title">Image and Signature</h2>
        {/* <button className='log-btn button-primary' onClick={handleNext}>Save & Next</button> */}
      </div>
      <div className="file-upload-container">
        <form>
          <div className="file-input">
            <label htmlFor="photo">
              <span>
                Upload Your Passport size Photo <br />
                <i>
                  (Image should not greater than 50kb & Image should be in the
                  format of jpg/jpeg)
                </i>
              </span>
              <br />
              <span>
                தங்களின் பாஸ்போர்ட் அளவு புகைப்படத்தை பதிவேற்றம் செய்க
                <br />
                <i>(புகைப்படம் 50kb க்கு மிகாமல் இருக்க வேண்டும்)</i>
              </span>
            </label>
            {!photoPreview ? (
              <input
                type="file"
                id="photo"
                name="photo"
                accept="image/*"
                onChange={handleFileChange}
                required
                disabled={photoPreview}
              />
            ) : (
              <>
                <input
                  style={{ marginTop: "40px" }}
                  className="fileUpload-inp"
                  type="text"
                  value="Uploaded"
                  disabled
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "center",
                    width: "100%",
                    margin: "10px 0px",
                  }}
                >
                  <input
                    type="button"
                    className="deleteButton"
                    onClick={() => handleFileDelete(photoPreview, "photo")}
                    value="Delete"
                  />
                </div>
              </>
            )}
          </div>
          {/* <div className="file-input">
            <label htmlFor="photo">
              <span>
                Upload Your Passport size Photo <br />
                <i>
                  (Image should not greater than 50kb && Image should be in the
                  format of jpg/jpeg/png)
                </i>
              </span>
              <br />
              <span>
                தங்கள் பாஸ்போர்ட் அளவு புகைப்படத்தை பதிவேற்றம் செய்க
                <br />
                <i>(புகைப்படம் 50kb க்கு மிகாமல் இருக்க வேண்டும்)</i>
              </span>
            </label>
            <input
              type="file"
              id="photo"
              name="photo"
              accept="image/*"
              onChange={handleFileChange}
              required
            />
          </div> */}
          <div className="file-input">
            <label htmlFor="signature">
              <span>
                Upload Your Signature
                <br />
                <i>
                  (Image should not greater than 30kb & Image should be in the
                  format of jpg/jpeg)
                </i>
              </span>
              <br />
              <span>
                தங்களின் கையொப்பத்தை பதிவேற்றம் செய்க
                <br />
                <i>(கையொப்பம் 30kb க்கு மிகாமல் இருக்க வேண்டும்)</i>
              </span>
            </label>
            {!signaturePreview ? (
              <input
                type="file"
                id="signature"
                name="signature"
                accept="image/*"
                onChange={handleFileChange}
                required
                disabled={signaturePreview}
              />
            ) : (
              <>
                <input
                  style={{ marginTop: "40px" }}
                  className="fileUpload-inp"
                  type="text"
                  value="Uploaded"
                  disabled
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "center",
                    width: "100%",
                    margin: "10px 0px",
                  }}
                >
                  <input
                    type="button"
                    className="deleteButton"
                    onClick={() => handleFileDelete(signaturePreview, "sign")}
                    value="Delete"
                  />
                </div>
              </>
            )}
          </div>
          {/* <div className="file-input">
            <label htmlFor="signature">
              <span>
                Upload Your Signature
                <br />
                <i>
                  (Image should not greater than 30kb && Image should be in the
                  format of jpg/jpeg/png)
                </i>
              </span>
              <br />
              <span>
                தங்கள் பாஸ்போர்ட் அளவு கையெழுத்தை பதிவேற்றம் செய்க
                <br />
                <i>(புகைப்படம் 30kb க்கு மிகாமல் இருக்க வேண்டும்)</i>
              </span>
            </label>
            <input
              type="file"
              id="signature"
              name="signature"
              accept="image/*"
              onChange={handleFileChange}
              required
            />
          </div> */}
          <div className="file-upload-btn-container">
            {/* <button className='button-tertiary file-upload-btn' type="submit">Upload</button> */}
          </div>
        </form>
        <div className="image-preview">
          <div>
            <h3>Photo Preview:</h3>
            {photoPreview && (
              <img
                src={photoPreview}
                alt="Preview"
                onError={(e) => {
                  e.target.onerror = null;
                  setPhotoPreview("");
                  setModalType({
                    icon: "warning",
                    title: "Invalid Photo",
                    text: "Uploaded photo could not be loaded. Please upload again.",
                  });
                  openModal();
                  setOnConfirm(() => closeModal);
                }}
              />
            )}
          </div>
          <div>
            <h3>Signature Preview:</h3>
            {signaturePreview && (
              <img
                src={signaturePreview}
                alt="Signature Preview"
                onError={(e) => {

                  e.target.onerror = null;
                  setSignaturePreview("");
                  setModalType({
                    icon: "warning",
                    title: "Invalid Signature",
                    text: "Uploaded signature could not be loaded. Please upload again.",
                  });
                  openModal();
                  setOnConfirm(() => closeModal);
                }}
              />
            )}
          </div>
        </div>
      </div>
      <br />
      <div className="next-button">
        <button className="log-btn button-secondary" onClick={handlePrevious}>
          Previous
        </button>
        <button
          className="log-btn button-primary"
          onClick={handleImageUploadSubmit}
        >
          Save & Next
        </button>
      </div>
      <div
        style={{
          position: "relative",
          alignSelf: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PopModal
          isOpen={isModalOpen}
          type={modalType}
          confirmButtonText="OK"
          onConfirm={onConfirm}
          showCancelButton={showCancelButton}
          cancelButtonText="Cancel"
          onCancel={onCancel}
        />
      </div>
    </section>
  );
}

export default ImagesUpload;
